import type { StoreApi } from "zustand";

import type {
  AttributeCollection,
  FieldId,
  FieldValidationError,
  FieldValue,
  SelfServiceTaskForm,
} from "../../../types";
import { createFieldId, parseFieldId } from "../../../utils/field-id";
import { validateField as validateFieldUtil } from "../../../utils/validation-rules";
import type {
  FormStore,
  SliceSetFunction,
  ValidationActions,
} from "../../types";

/**
 * フォームバリデーションに関するアクション
 * - エラー管理
 * - フィールドバリデーション
 */
export const createValidationActions = (
  set: SliceSetFunction,
  get: () => FormStore,
  _store: StoreApi<FormStore>,
): ValidationActions => ({
  /**
   * エラー一覧を設定
   */
  setErrors: (errors: FieldValidationError[]) => {
    set({ errors }, false, "validation/setErrors");
  },

  /**
   * 単一フィールドのエラーを設定
   */
  setFieldError: (fieldId: FieldId, message: string) => {
    set(
      (state) => {
        const exists = state.errors.some((e) => e.fieldId === fieldId);

        if (exists) {
          return {
            ...state,
            errors: state.errors.map((e) =>
              e.fieldId === fieldId ? { fieldId, message } : e,
            ),
          };
        } else {
          return {
            ...state,
            errors: [...state.errors, { fieldId, message }],
          };
        }
      },
      false,
      "validation/setFieldError",
    );
  },

  /**
   * フィールドのエラーをクリア
   */
  removeFieldError: (fieldId: FieldId) => {
    set(
      (state) => ({
        ...state,
        errors: state.errors.filter((e) => e.fieldId !== fieldId),
      }),
      false,
      "validation/removeFieldError",
    );
  },

  /**
   * 単一フィールドのバリデーションを実行する
   */
  validateField: (fieldId: FieldId): boolean => {
    const state = get();

    // エラーをクリアしてから再検証する
    state.removeFieldError(fieldId);

    // フィールドが無効化されたコレクションに属しているかチェック
    const parsedId = parseFieldId(fieldId);
    if (parsedId) {
      const isDisabled = state.disabledCollections.some(
        (item) =>
          item.collectionId === parsedId.collectionId &&
          item.collectionIndex === parsedId.collectionIndex,
      );

      if (isDisabled) {
        return true; // 無効化されたコレクションのフィールドは常に有効とみなす
      }
    }

    const value = state.getFieldValue(fieldId);
    const attribute = state.getAttribute(fieldId);

    if (!attribute) {
      return true; // 属性が見つからない場合は有効とみなす
    }

    const validationResult = validateFieldUtil(attribute, value);
    if (!validationResult.isValid) {
      state.setFieldError(fieldId, validationResult.errorMessage || "");
      return false;
    }

    return true;
  },

  /**
   * フォームのバリデーションを実行してエラーを設定する
   * @returns バリデーションが成功した場合はtrue、失敗した場合はfalseを返す
   */
  validateAll: () => {
    const { form, values, disabledCollections } = get();

    if (!form) {
      return true;
    }

    // 全てのフィールドIDを取得
    const allFieldIds = getAllFieldIds(form, values, disabledCollections);

    // 全てのフィールドをタッチ済み状態にする（エラー表示のため）
    allFieldIds.forEach((fieldId) => {
      get().setTouched(fieldId, true);
    });

    // 全てのフィールドのバリデーションを実行
    let hasErrors = false;
    allFieldIds.forEach((fieldId) => {
      // ストアの validateField メソッドを使用
      const isValid = get().validateField(fieldId);
      if (!isValid) {
        hasErrors = true;
      }
    });

    return !hasErrors;
  },
});

/**
 * フォーム定義から全てのフィールドIDを取得する
 */
const getAllFieldIds = (
  form: SelfServiceTaskForm,
  values: FieldValue[],
  disabledCollections: Array<{
    collectionId: string;
    collectionIndex: number;
  }>,
): FieldId[] => {
  const allFieldIds: FieldId[] = [];

  if (!form?.form?.attributeCollections) {
    return allFieldIds;
  }

  // attributeCollectionsをidでグループ化
  const groupedCollections = form.form.attributeCollections.reduce<
    Record<string, AttributeCollection[]>
  >((acc, collection) => {
    (acc[collection.id] ??= []).push(collection);
    return acc;
  }, {});

  Object.values(groupedCollections).forEach(
    (collectionGroup: AttributeCollection[]) => {
      collectionGroup.forEach((collection, collectionIdx) => {
        // 無効化されたコレクションはスキップ
        const isDisabled = disabledCollections.some(
          (item) =>
            item.collectionId === collection.id &&
            item.collectionIndex === collectionIdx,
        );

        if (isDisabled) {
          return; // このコレクションをスキップ
        }

        collection.attributeGroupSettings?.forEach((group) => {
          // グループが動的な場合、valuesから最大のgroupIndexを取得
          const maxGroupIndex = group.isDynamic
            ? Math.max(
                ...values
                  .filter((v) => {
                    const params = parseFieldId(v.fieldId);
                    return (
                      params &&
                      params.collectionId === collection.id &&
                      params.collectionIndex === collectionIdx &&
                      params.groupId === group.id
                    );
                  })
                  .map((v) => {
                    const params = parseFieldId(v.fieldId);
                    return params ? params.groupIndex : 0;
                  }),
                -1,
              )
            : 0;

          // 各グループインデックスに対してフィールドを生成
          Array.from({ length: maxGroupIndex + 1 }, (_, groupIndex) => {
            group.attributes?.forEach((attr) => {
              const fieldId = createFieldId({
                collectionId: collection.id,
                collectionIndex: collectionIdx,
                groupId: group.id,
                groupIndex,
                attributeId: attr.id,
              });
              allFieldIds.push(fieldId);
            });
          });
        });
      });
    },
  );

  return allFieldIds;
};
